/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable react-hooks/exhaustive-deps */
// TODO: Fix TypeScript any types and improve type safety
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useBestPrice } from '../hooks/useBestPrice';
import { useLocationStore } from '@/features/location/store/location-store';
import { useLocationInformation } from '@/features/location/hooks/useLocationInformation';
import { useChargePointsStore } from '@/features/charge-points/store/charge-points-store';
import { useGoogleMapsStore } from '@/features/map/store/google-maps-store';

interface ChargePointResult {
  id: string;
  numericId: number;
  name: string;
  price_per_kwh: number | null;
  price_label: string;
  latitude: number;
  longitude: number;
  address: string | undefined;
  currency: any;
  maxKwLabel: string | undefined;
  typeLabel: string | undefined;
  operatorName?: string;
  connectors: Array<{
    id: number;
    identifier: string;
    name: string;
    iconUrl?: string;
  }>;
}

interface BestPriceModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const KM_TO_MILES = 0.621371;
const MILES_TO_KM = 1.60934;

// Locales that use imperial units (miles)
const IMPERIAL_LOCALES = ['en-US', 'en-GB', 'en-us', 'en-gb'];

export function BestPriceModal({ isOpen, onClose }: BestPriceModalProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  // Determine if current locale uses imperial units
  const useImperial = IMPERIAL_LOCALES.includes(i18n.language);
  const distanceUnit = useImperial ? t('bestPrice.miles') : t('bestPrice.km');

  // Default radius: 5 units in display (5 km or 5 miles depending on locale)
  const defaultDisplayRadius = 5;
  const defaultRadiusKm = useImperial
    ? defaultDisplayRadius * MILES_TO_KM
    : defaultDisplayRadius;
  const [radiusKm, setRadiusKm] = useState(defaultRadiusKm); // Always store in km for API
  const [sliderValue, setSliderValue] = useState(defaultRadiusKm); // Immediate slider value for display

  // Use map center for distance calculations
  const mapCenter = useLocationStore((s) => s.mapCenter);

  const { data: locationInfo } = useLocationInformation();
  const searchCenterAddress = locationInfo?.formattedAddress;

  const chargePointCache = useChargePointsStore((s: any) => s.chargePointCache);
  const selectChargePoint = useChargePointsStore(
    (s: any) => s.selectChargePoint
  );
  const mapInstance = useGoogleMapsStore((s) => s.mapInstance);
  const isMapReady = useGoogleMapsStore((s) => s.isMapReady);

  const {
    data: results,
    isLoading,
    refetch,
  } = useBestPrice({
    enabled: isOpen && !!mapCenter,
    radius: radiusKm,
  });

  // Debounce radius changes (700ms to match other filters)
  useEffect(() => {
    const timer = setTimeout(() => {
      setRadiusKm(sliderValue);
    }, 700);

    return () => clearTimeout(timer);
  }, [sliderValue]);

  // Auto-search when modal opens (map center is always available)
  // Also refresh location info to update address field immediately
  useEffect(() => {
    if (isOpen && mapCenter) {
      // Force refresh location information to update address immediately
      // (bypasses the 2-second debounce)
      void queryClient.invalidateQueries({
        queryKey: ['location-information-cached'],
      });

      refetch();
    }
  }, [isOpen, mapCenter, refetch, queryClient]);

  // ESC key handler
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const handleClose = () => {
    onClose();
  };

  const handleChargePointClick = (cp: ChargePointResult) => {
    // Find the charge point wrapper in the cache
    const wrapper = chargePointCache.get(cp.id);
    if (wrapper && wrapper.chargePoint) {
      // Select the charge point
      selectChargePoint(wrapper);

      // Pan map to charge point location
      if (isMapReady && mapInstance) {
        mapInstance.panTo({ lat: cp.latitude, lng: cp.longitude });
        mapInstance.setZoom(18);
      }

      // Close the best price modal
      onClose();
    }
  };

  // Convert km to display value (miles or km)
  const getDisplayDistance = (km: number) => {
    return useImperial ? km * KM_TO_MILES : km;
  };

  // Convert slider value to km (slider shows miles if imperial, km otherwise)
  const handleSliderChange = (value: number) => {
    const km = useImperial ? value * MILES_TO_KM : value;
    setSliderValue(km); // Update slider immediately, debounced effect will update radiusKm
  };

  if (!isOpen) return null;

  // Extract charge points and groups from transformed API response
  // Note: At high zoom levels, API returns groups instead of individual charge points
  const allChargePointsInArea = (results?.single_list || []).filter(
    (item) =>
      (item.type === 'chargePoint' && item.chargePoint) ||
      (item.type === 'chargePointGroup' && item.chargePointGroup)
  );

  const chargePoints = allChargePointsInArea
    .map((item) => {
      // Handle both charge points and groups
      const entity = item.chargePoint || item.chargePointGroup!;

      let pricePerKwh: number | null = null;
      let priceLabel = '';

      // Try to get price from pricingCollection (detail API - only for charge points)
      if (item.chargePoint) {
        const masterPricing =
          item.chargePoint.pricingCollection?.pricingsPublic?.masterPricing;
        if (masterPricing) {
          pricePerKwh =
            masterPricing.amount && masterPricing.amount > 0
              ? masterPricing.amount
              : (masterPricing.amountMin ?? null);
          priceLabel = masterPricing.label || '';
        }
      }

      // Fallback: Parse from priceLabel (map API - works for both charge points and groups)
      // Examples: "4.24 DKK/kWh", ",72 DKK/kWh", "4.522 - 6.0515 DKK" (range for groups)
      if (!pricePerKwh && entity.priceLabel) {
        priceLabel = entity.priceLabel;
        // Fix malformed labels like ",72" -> "0.72"
        const fixedLabel = priceLabel.replace(/^,(\d+)/, '0.$1');
        // Extract numeric value (handles formats like "4.24 DKK/kWh", "4,24 DKK", etc.)
        // For ranges like "4.522 - 6.0515 DKK", extract the first (minimum) value
        const match = fixedLabel.match(/^[\s]*([\d.,]+)/);
        if (match && match[1]) {
          const numStr = match[1].replace(',', '.');
          const num = parseFloat(numStr);
          if (!isNaN(num) && num > 0) {
            pricePerKwh = num;
          }
        }
      }

      return {
        id: item.id,
        numericId: entity.id,
        name: entity.name,
        price_per_kwh: pricePerKwh,
        price_label: priceLabel,
        latitude: item.coordinate.lat,
        longitude: item.coordinate.lng,
        address: entity.address1 || entity.city,
        currency: entity.currency,
        maxKwLabel: entity.maxKwLabel,
        typeLabel: entity.typeLabel,
        operatorName: entity.operatorName,
        connectors: entity.connectors || [],
      };
    })
    .filter((cp) => cp.price_per_kwh != null && cp.price_per_kwh > 0);

  const hasChargePointsButNoPricing =
    allChargePointsInArea.length > 0 && chargePoints.length === 0;

  const sortedChargePoints = [...chargePoints]
    .filter((cp) => cp.price_per_kwh != null && cp.price_per_kwh > 0)
    .sort((a, b) => (a.price_per_kwh || 0) - (b.price_per_kwh || 0));

  const averagePrice =
    sortedChargePoints.length > 0
      ? sortedChargePoints.reduce(
          (sum, cp) => sum + (cp.price_per_kwh || 0),
          0
        ) / sortedChargePoints.length
      : 0;

  // Get currency from first charge point or default to EUR
  const currency = chargePoints[0]?.currency?.identifier || 'EUR';

  // Parse price from label (e.g., "4.24 DKK/kWh" -> {value: "4.24", unit: "DKK/kWh"})
  // Also handles malformed labels like ",72 DKK/kWh" (missing leading zero)
  const parsePriceLabel = (label: string) => {
    // Fix malformed labels that start with comma (e.g., ",72" -> "0.72")
    const fixedLabel = label.replace(/^,(\d+)/, '0.$1');
    const match = fixedLabel.match(/([\d.,]+)\s*(.+)/);
    if (match && match[1] && match[2]) {
      // Normalize decimal separator (some locales use comma)
      const normalizedValue = match[1].replace(',', '.');
      // Parse as number and round to 2 decimal places for display
      const numericValue = parseFloat(normalizedValue);
      if (!isNaN(numericValue)) {
        return { value: numericValue.toFixed(2), unit: match[2] };
      }
      return { value: normalizedValue, unit: match[2] };
    }
    return null;
  };

  const calculateDistance = (cp: ChargePointResult) => {
    if (!mapCenter) return 0;
    const R = 6371; // Earth's radius in km
    const dLat = ((cp.latitude - mapCenter.lat) * Math.PI) / 180;
    const dLon = ((cp.longitude - mapCenter.lng) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((mapCenter.lat * Math.PI) / 180) *
        Math.cos((cp.latitude * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-surface rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="bg-ink px-6 py-5 text-surface">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <svg
                  className="w-7 h-7"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                {t('bestPrice.title')}
              </h2>
              <p className="text-white/90 text-sm mt-1">
                {t('bestPrice.subtitle')}
              </p>
              {searchCenterAddress && (
                <div className="mt-2 flex items-start gap-2 text-white/80 text-sm">
                  <svg
                    className="w-4 h-4 mt-0.5 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  <span className="font-medium">{searchCenterAddress}</span>
                </div>
              )}
            </div>
            <button
              onClick={handleClose}
              className="text-white/90 hover:text-white hover:bg-white/10 rounded-lg p-2 transition-colors"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Radius Selection Slider */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-ink mb-3">
              {t('bestPrice.radiusLabel')}:{' '}
              {getDisplayDistance(sliderValue).toFixed(0)} {distanceUnit}
            </label>
            <input
              type="range"
              min={1}
              max={useImperial ? 100 : 100}
              value={Math.round(getDisplayDistance(sliderValue))}
              onChange={(e) => handleSliderChange(Number(e.target.value))}
              className="w-full h-2 bg-ink/10 rounded-lg appearance-none cursor-pointer accent-monta-blue"
              style={{
                background: `linear-gradient(to right, #302ee0 0%, #302ee0 ${(getDisplayDistance(sliderValue) / 100) * 100}%, #E5E5EA ${(getDisplayDistance(sliderValue) / 100) * 100}%, #E5E5EA 100%)`,
              }}
            />
            <div className="flex justify-between text-xs text-muted mt-1">
              <span>1 {distanceUnit}</span>
              <span>100 {distanceUnit}</span>
            </div>
          </div>

          {/* Results */}
          <div className="space-y-4">
            {isLoading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-monta-blue border-t-transparent" />
                <p className="mt-4 text-muted">{t('bestPrice.loading')}</p>
              </div>
            ) : results && sortedChargePoints.length === 0 ? (
              <div className="text-center py-12">
                <svg
                  className="w-16 h-16 mx-auto text-muted/70 mb-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <p className="text-muted font-medium">
                  {hasChargePointsButNoPricing
                    ? t('bestPrice.noPricing')
                    : t('bestPrice.noResults')}
                </p>
                <p className="text-sm text-muted mt-1">
                  {hasChargePointsButNoPricing
                    ? t('bestPrice.noPricingDescription')
                    : t('bestPrice.tryIncreaseRadius')}
                </p>
                <button
                  onClick={() => refetch()}
                  disabled={isLoading}
                  className="mt-4 text-monta-blue hover:text-monta-blue-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <div className="inline-block animate-spin rounded-full h-4 w-4 border-2 border-monta-blue border-t-transparent" />
                      <span>{t('bestPrice.loading')}</span>
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                        />
                      </svg>
                      <span>{t('bestPrice.searchAgain')}</span>
                    </>
                  )}
                </button>
              </div>
            ) : results && sortedChargePoints.length > 0 ? (
              <>
                {/* Results Count */}
                <div className="text-sm text-muted mb-2 flex items-center gap-2">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                    />
                  </svg>
                  <span className="font-medium">
                    {sortedChargePoints.length} {t('bestPrice.resultsFound')}
                  </span>
                </div>

                {/* Results List - Scrollable */}
                <div className="max-h-[450px] overflow-y-auto space-y-3 pr-2 pt-2 pl-4 scroll-smooth [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-monta-gray-100 [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-thumb]:bg-monta-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-monta-gray-400">
                  {sortedChargePoints.map((cp, index) => {
                    const distance = calculateDistance(cp);
                    const savings =
                      ((averagePrice - (cp.price_per_kwh || 0)) /
                        averagePrice) *
                      100;
                    const isBestPrice = index === 0;
                    const parsedPrice = parsePriceLabel(cp.price_label);
                    const rank = index + 1;

                    return (
                      <div
                        key={cp.id}
                        onClick={() => handleChargePointClick(cp)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleChargePointClick(cp);
                          }
                        }}
                        className={`relative rounded-xl p-4 transition-all cursor-pointer hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] ${
                          isBestPrice
                            ? 'bg-gradient-to-r from-yellow-50 to-amber-50 border-2 border-yellow-400 ring-2 ring-yellow-200 hover:border-yellow-500'
                            : 'bg-white border border-monta-gray-200 hover:border-monta-blue'
                        }`}
                      >
                        {/* Rank Badge */}
                        <div
                          className={`absolute -left-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm shadow-lg pointer-events-none ${
                            isBestPrice
                              ? 'bg-gradient-to-br from-yellow-400 to-amber-500 text-white'
                              : rank === 2
                                ? 'bg-gradient-to-br from-gray-300 to-gray-400 text-gray-700'
                                : rank === 3
                                  ? 'bg-gradient-to-br from-amber-600 to-amber-700 text-white'
                                  : 'bg-monta-gray-200 text-ink/80'
                          }`}
                        >
                          #{rank}
                        </div>

                        {isBestPrice && (
                          <div className="absolute -top-3 left-4 bg-gradient-to-r from-yellow-400 to-amber-500 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1 shadow-lg">
                            <svg
                              className="w-4 h-4"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                            {t('bestPrice.bestPrice')}
                          </div>
                        )}

                        <div className="flex items-start justify-between gap-4 pl-6">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-bold text-ink truncate">
                              {cp.name}
                            </h3>
                            {cp.operatorName && (
                              <p className="text-xs text-muted truncate mt-0.5 font-medium">
                                {cp.operatorName}
                              </p>
                            )}
                            {cp.address && (
                              <p className="text-sm text-muted truncate mt-1">
                                {cp.address}
                              </p>
                            )}

                            {/* Technical Specs */}
                            <div className="flex items-center gap-2 mt-2 text-xs text-ink/80">
                              {cp.typeLabel && (
                                <>
                                  <span className="font-medium">
                                    {cp.typeLabel}
                                  </span>
                                  <span className="text-muted/70">•</span>
                                </>
                              )}
                              {cp.maxKwLabel && <span>{cp.maxKwLabel}</span>}
                              {cp.connectors.length > 0 && cp.maxKwLabel && (
                                <span className="text-muted/70">•</span>
                              )}
                              {cp.connectors.length > 0 && (
                                <span>
                                  {cp.connectors.map((c) => c.name).join(', ')}
                                </span>
                              )}
                            </div>

                            <div className="flex items-center gap-3 mt-2">
                              <span className="inline-flex items-center gap-1 text-xs bg-blue-100 text-muted px-2 py-1 rounded-full">
                                <svg
                                  className="w-3 h-3"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                                  />
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                                  />
                                </svg>
                                {getDisplayDistance(distance).toFixed(1)}{' '}
                                {distanceUnit}
                              </span>
                              {savings > 5 && (
                                <span className="inline-flex items-center gap-1 text-xs bg-ok/15 text-ok px-2 py-1 rounded-full font-medium">
                                  <svg
                                    className="w-3 h-3"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                                    />
                                  </svg>
                                  {savings.toFixed(0)}% {t('bestPrice.cheaper')}
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="text-right flex-shrink-0">
                            {parsedPrice ? (
                              <>
                                <div
                                  className={`text-3xl font-bold ${isBestPrice ? 'text-amber-600' : 'text-green-600'}`}
                                >
                                  {parsedPrice.value}
                                </div>
                                <div className="text-xs text-muted mt-1">
                                  {parsedPrice.unit}
                                </div>
                              </>
                            ) : (
                              <>
                                <div
                                  className={`text-3xl font-bold ${isBestPrice ? 'text-amber-600' : 'text-green-600'}`}
                                >
                                  {cp.price_per_kwh?.toFixed(2)}
                                </div>
                                <div className="text-xs text-muted mt-1">
                                  {currency}/kWh
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Search Again */}
                <button
                  onClick={() => refetch()}
                  disabled={isLoading}
                  className="w-full mt-4 text-monta-blue hover:text-monta-blue-600 font-medium py-2 hover:bg-ink/5 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <div className="inline-block animate-spin rounded-full h-4 w-4 border-2 border-monta-blue border-t-transparent" />
                      <span>{t('bestPrice.loading')}</span>
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                        />
                      </svg>
                      <span>{t('bestPrice.searchAgain')}</span>
                    </>
                  )}
                </button>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
